import type { ObjectPeekDto } from "@excellent/ui/api/contracts/objects";
import type {
  VerificationControlDto,
} from "@excellent/ui/api/contracts/verification-define";
import type {
  VerificationEvidenceArtifactDto,
  VerificationEvidenceLineageEventDto,
} from "@excellent/ui/api/contracts/verification-evidence";
import type {
  VerificationPackDto,
  VerificationReceiptDto,
} from "@excellent/ui/api/contracts/verification-prove";
import type { VerificationEnvironment } from "./env";

export type VerificationInspectorKind = "control" | "artifact" | "receipt" | "pack";

export type VerificationInspectorRef = {
  kind: VerificationInspectorKind;
  id: string;
  environment: VerificationEnvironment;
};

export type InspectorFact = {
  label: string;
  value: string;
};

export type InspectorObservation = {
  id: string;
  label: string;
  summary?: string | null;
  actor?: string | null;
  occurredAt?: string | null;
};

export type InspectorObject = {
  id: string;
  title: string;
  kind: string;
  state: string;
  description: string;
  limitation: string;
  facts: InspectorFact[];
  observationsSummary: string;
  observations: InspectorObservation[];
  timestamps: InspectorFact[];
  interpretation: InspectorFact[];
  technical: InspectorFact[];
  href: string;
};

const INSPECTOR_PREFIX = "verification";

const DETAIL_KIND_BY_PATH = {
  controls: "control",
  evidence: "artifact",
  receipts: "receipt",
  packs: "pack",
} as const satisfies Record<string, VerificationInspectorKind>;

const DETAIL_PATH_BY_KIND: Record<VerificationInspectorKind, string> = {
  control: "controls",
  artifact: "evidence",
  receipt: "receipts",
  pack: "packs",
};

function environmentFromId(id: string): VerificationEnvironment | null {
  if (id.length < 1 || id.length > 200) return null;
  if (id.startsWith("real:")) return "real";
  if (id.startsWith("demo:")) return "demo";
  return null;
}

export function verificationInspectorUid(
  kind: VerificationInspectorKind,
  id: string,
): string {
  return `${INSPECTOR_PREFIX}:${kind}:${id}`;
}

export function parseVerificationInspectorUid(uid: string): VerificationInspectorRef | null {
  const match = /^verification:(control|artifact|receipt|pack):(.+)$/.exec(uid);
  if (!match) return null;
  const kind = match[1] as VerificationInspectorKind;
  const id = match[2] ?? "";
  const environment = environmentFromId(id);
  return environment ? { kind, id, environment } : null;
}

export function isVerificationInspectorUid(uid: string): boolean {
  return uid.startsWith(`${INSPECTOR_PREFIX}:`);
}

export function verificationInspectorRefFromHref(to: string): VerificationInspectorRef | null {
  if (!to.startsWith("/") || to.startsWith("//")) return null;
  const url = new URL(to, "http://excellent.local");
  if (url.origin !== "http://excellent.local") return null;
  const parts = url.pathname.split("/").filter(Boolean);
  if (parts.length !== 2) return null;
  const kind = DETAIL_KIND_BY_PATH[parts[0] as keyof typeof DETAIL_KIND_BY_PATH];
  if (!kind) return null;
  let id: string;
  try {
    id = decodeURIComponent(parts[1] ?? "");
  } catch {
    return null;
  }
  const environment = environmentFromId(id);
  return environment ? { kind, id, environment } : null;
}

export function inspectorHrefForCurrentPage(
  pathname: string,
  currentSearch: string,
  destination: string,
): string | null {
  const ref = verificationInspectorRefFromHref(destination);
  if (!ref) return null;
  const params = new URLSearchParams(
    currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch,
  );
  const currentEnvironment = params.get("env");
  if (currentEnvironment && currentEnvironment !== ref.environment) return null;
  params.set("env", ref.environment);
  params.set("peek", verificationInspectorUid(ref.kind, ref.id));
  params.set("pane", "inspector");
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}`;
}

function baseInterpretation(scope: string): InspectorFact[] {
  return [
    { label: "Method", value: "Read the existing environment-scoped product record." },
    { label: "Policy", value: "No additional evaluation was performed by the Inspector." },
    { label: "Confidence", value: "Not scored by this read response." },
    { label: "Scope", value: scope },
    { label: "Falsifiers", value: "A newer source record or a failed source read can change this view." },
  ];
}

function technicalBase(id: string, href: string, environment: VerificationEnvironment): InspectorFact[] {
  return [
    { label: "Record ID", value: id },
    { label: "Canonical route", value: href },
    { label: "Environment", value: environment },
    { label: "Raw telemetry", value: "Not provided by this read response." },
  ];
}

export function projectWorkspaceObject(object: ObjectPeekDto): InspectorObject {
  return {
    id: object.uid,
    title: object.title,
    kind: object.kind.replace(/_/g, " "),
    state: object.state.replace(/_/g, " "),
    description: object.description || object.essence || "No description has been retained for this object.",
    limitation: "This is a stored workspace record. It does not, by itself, verify that its description, status, or outcome is true.",
    facts: [
      ...(object.owner?.name ? [{ label: "Owner", value: object.owner.name }] : []),
      ...object.vitals.map((vital) => ({ label: vital.label, value: vital.value })),
      ...(object.targetOutcome ? [{ label: "Intended outcome", value: object.targetOutcome }] : []),
    ],
    observationsSummary: "The canonical object resolver returned this record and its retained workspace activity. No separate evidence envelope or receipt is included in this response.",
    observations: object.events.map((event) => ({
      id: event.uid,
      label: event.label,
      summary: event.summary,
      actor: event.actorLabel,
      occurredAt: event.occurredAt,
    })),
    timestamps: [
      { label: "Record created", value: object.createdAt },
      { label: "Last updated", value: object.updatedAt },
      ...(object.fulfilledAt ? [{ label: "Marked fulfilled", value: object.fulfilledAt }] : []),
    ],
    interpretation: [
      { label: "Method", value: "Read the canonical workspace object projection and its retained activity." },
      { label: "Policy", value: "No evaluation policy is included in this object response." },
      { label: "Confidence", value: "Not scored by this object response." },
      { label: "Scope", value: "This object in the current workspace; linked sources were not inspected." },
      { label: "Falsifiers", value: "Not specified by the source record." },
    ],
    technical: [
      { label: "UID", value: object.uid },
      { label: "Canonical route", value: object.href },
      { label: "Returned events", value: `${object.events.length} of ${object.eventTotal}` },
      { label: "Raw telemetry", value: "Not provided by this endpoint." },
      { label: "Hash", value: "Not provided by this endpoint." },
      { label: "Signature", value: "Not provided by this endpoint." },
      { label: "Producer version", value: "Not provided by this endpoint." },
    ],
    href: object.href,
  };
}

export function projectControl(
  control: VerificationControlDto,
  environment: VerificationEnvironment,
): InspectorObject {
  const href = `/${DETAIL_PATH_BY_KIND.control}/${encodeURIComponent(control.id)}`;
  return {
    id: control.id,
    title: control.title,
    kind: "control",
    state: control.status,
    description: `${control.ref} organizes ${control.framework} ${control.family} evidence and decisions.`,
    limitation: "This control status is a product projection. Open findings, source coverage, and the active policy still need review before relying on it for an audit.",
    facts: [
      { label: "Reference", value: control.ref },
      { label: "Framework", value: control.framework },
      { label: "Family", value: control.family },
      { label: "Frequency", value: control.frequency },
      { label: "Consequence", value: control.consequence },
      { label: "Population", value: String(control.populationSize) },
      { label: "Sampled", value: String(control.sampledSize) },
      { label: "Open findings", value: String(control.openFindingCount) },
    ],
    observationsSummary: `This read returned ${control.sourceIds.length} linked source reference${control.sourceIds.length === 1 ? "" : "s"}. It did not fetch the source records or their evidence.`,
    observations: control.sourceIds.map((sourceId) => ({
      id: sourceId,
      label: "Linked source reference",
      summary: sourceId,
    })),
    timestamps: [],
    interpretation: baseInterpretation("This control record and its named references in the selected environment."),
    technical: [
      ...technicalBase(control.id, href, environment),
      { label: "Active policy", value: control.activePolicyId ?? "Not set" },
      { label: "Owner actor", value: control.ownerActorId ?? "Not set" },
      { label: "Hash", value: "Not provided by the control read response." },
      { label: "Signature", value: "Not provided by the control read response." },
    ],
    href,
  };
}

export function projectArtifact(
  artifact: VerificationEvidenceArtifactDto,
  environment: VerificationEnvironment,
  lineage: VerificationEvidenceLineageEventDto[] | undefined,
): InspectorObject {
  const href = `/${DETAIL_PATH_BY_KIND.artifact}/${encodeURIComponent(artifact.id)}`;
  return {
    id: artifact.id,
    title: artifact.label,
    kind: "evidence artifact",
    state: artifact.state,
    description: `A ${artifact.sourceKind} artifact collected from ${artifact.sourceId}.`,
    limitation: "The record carries content and lineage hashes, but this Inspector read does not establish immutability, source authenticity, or independent verification.",
    facts: [
      { label: "Source", value: artifact.sourceId },
      { label: "Collector", value: artifact.collectorActorId },
      { label: "Observations", value: String(artifact.observationCount) },
      { label: "Controls", value: String(artifact.controlIds.length) },
      { label: "Receipt records", value: String(artifact.citedByReceiptIds.length) },
      { label: "Size", value: `${artifact.sizeBytes} bytes` },
    ],
    observationsSummary: lineage
      ? `The existing lineage read returned ${lineage.length} recorded event${lineage.length === 1 ? "" : "s"}.`
      : "Lineage details were not available from this read. The artifact observation count is metadata, not the observations themselves.",
    observations: (lineage ?? []).map((event) => ({
      id: event.id,
      label: event.kind.replace(/_/g, " "),
      summary: event.reason,
      actor: event.actorId,
      occurredAt: event.occurredAt,
    })),
    timestamps: [
      { label: "Collected", value: artifact.collectedAt },
      ...(artifact.collectionQueuedAt ? [{ label: "Collection requested", value: artifact.collectionQueuedAt }] : []),
      ...(artifact.redactedAt ? [{ label: "Redaction recorded", value: artifact.redactedAt }] : []),
    ],
    interpretation: baseInterpretation("This artifact record and its returned lineage in the selected environment."),
    technical: [
      ...technicalBase(artifact.id, href, environment),
      { label: "Content hash", value: artifact.contentHash },
      { label: "Encrypted at rest", value: artifact.encryptedAtRest ? "Recorded as true" : "Not recorded" },
      { label: "Signature", value: "Not provided by the artifact read response." },
    ],
    href,
  };
}

export function projectReceipt(
  receipt: VerificationReceiptDto,
  environment: VerificationEnvironment,
): InspectorObject {
  const href = `/${DETAIL_PATH_BY_KIND.receipt}/${encodeURIComponent(receipt.id)}`;
  return {
    id: receipt.id,
    title: receipt.id,
    kind: "receipt record",
    state: receipt.status,
    description: `A recorded result for run ${receipt.runId} with ${receipt.checkCount} check${receipt.checkCount === 1 ? "" : "s"}.`,
    limitation: "This response does not include signature bytes or trust material. The Inspector cannot claim that this receipt is signed, replayable, or independently verified.",
    facts: [
      { label: "Run", value: receipt.runId },
      { label: "Checks", value: String(receipt.checkCount) },
      { label: "Evidence bindings", value: String(receipt.evidenceHashes.length) },
      { label: "Policy versions", value: String(receipt.policyVersionRefs.length) },
      { label: "Supersedes", value: receipt.supersedes ?? "None" },
    ],
    observationsSummary: "Evidence hashes and policy version references are retained as bindings. Their source records were not fetched or verified by this Inspector read.",
    observations: [],
    timestamps: [
      { label: "Issued", value: receipt.issuedAt },
      ...(receipt.replayCheckedAt ? [{ label: "Compatibility replay metadata", value: receipt.replayCheckedAt }] : []),
    ],
    interpretation: baseInterpretation("This receipt metadata record in the selected environment."),
    technical: [
      ...technicalBase(receipt.id, href, environment),
      { label: "Payload hash", value: receipt.payloadHash },
      { label: "Engine version", value: receipt.engineVersion },
      { label: "Signature algorithm metadata", value: receipt.signatureAlgorithm },
      { label: "Signature key ID metadata", value: receipt.signatureKeyId },
      { label: "Signature verification", value: "Unavailable from this response." },
      { label: "Evidence hashes", value: receipt.evidenceHashes.join(", ") || "None" },
      { label: "Policy version refs", value: receipt.policyVersionRefs.join(", ") || "None" },
    ],
    href,
  };
}

export function projectPack(
  pack: VerificationPackDto,
  environment: VerificationEnvironment,
): InspectorObject {
  const href = `/${DETAIL_PATH_BY_KIND.pack}/${encodeURIComponent(pack.id)}`;
  return {
    id: pack.id,
    title: pack.label,
    kind: "evidence pack",
    state: pack.expiresAt && Date.parse(pack.expiresAt) <= Date.now() ? "expired" : "current record",
    description: `A ${pack.kind.replace(/_/g, " ")} pack record containing ${pack.runIds.length} run reference${pack.runIds.length === 1 ? "" : "s"}.`,
    limitation: "The bundle hash is consistency metadata. This response does not prove origin, tamper resistance, independent verification, or complete SOC 2 coverage.",
    facts: [
      { label: "Runs", value: String(pack.runIds.length) },
      { label: "Controls", value: String(pack.controlIds.length) },
      { label: "Receipt records", value: String(pack.receiptIds.length) },
      { label: "Artifact count", value: String(pack.artifactCount) },
      { label: "Scope limitations", value: String(pack.scopeLimitations.length) },
      { label: "Redaction records", value: String(pack.redactionCount) },
    ],
    observationsSummary: pack.scopeLimitations.length
      ? "The pack records scope limitations. Review them before sharing or relying on the pack."
      : "No scope limitations were returned by this read. That absence is not proof of complete coverage.",
    observations: pack.scopeLimitations.map((limitation, index) => ({
      id: `${pack.id}:limitation:${index}`,
      label: "Scope limitation",
      summary: limitation,
    })),
    timestamps: [
      { label: "Created", value: pack.createdAt },
      ...(pack.expiresAt ? [{ label: "Expires", value: pack.expiresAt }] : []),
      ...(pack.downloadedAt ? [{ label: "Download recorded", value: pack.downloadedAt }] : []),
    ],
    interpretation: baseInterpretation("This pack metadata record and its named references in the selected environment."),
    technical: [
      ...technicalBase(pack.id, href, environment),
      { label: "Bundle hash", value: pack.bundleHash },
      { label: "Size", value: `${pack.sizeBytes} bytes` },
      { label: "Run IDs", value: pack.runIds.join(", ") || "None" },
      { label: "Control IDs", value: pack.controlIds.join(", ") || "None" },
      { label: "Receipt IDs", value: pack.receiptIds.join(", ") || "None" },
      { label: "Signature", value: "Not provided by the pack read response." },
    ],
    href,
  };
}
